<template>
  <!-- Lagerplatzanlage mobil (Trello 21.08.): Liste + Filter, Bearbeiten-Sheet, "+" fix oben rechts -->
  <div class="m-list">
    <button v-if="canCreate" class="m-wms-fab" @click="openCreate"><i class="material-icons">add</i></button>

    <div class="m-search">
      <i class="material-icons">search</i>
      <input v-model="query" :placeholder="$t('mobile.wms.searchLocation')" type="search" />
      <button v-if="query" class="m-search__clear" @click="query = ''"><i class="material-icons">close</i></button>
    </div>
    <div class="m-wms-toggles">
      <label><input v-model="statusFilter" type="radio" :value="null" /> {{ $t("mobile.all") }}</label>
      <label><input v-model="statusFilter" type="radio" :value="true" /> {{ $t("mobile.wms.active") }}</label>
      <label><input v-model="statusFilter" type="radio" :value="false" /> {{ $t("mobile.wms.archived") }}</label>
    </div>

    <div v-if="loading" class="m-hint">{{ $t("mobile.loading") }}</div>
    <div v-else-if="!filtered.length" class="m-hint">{{ $t("mobile.noResults") }}</div>

    <table v-if="filtered.length" class="m-wms-table">
      <thead>
        <tr>
          <th>ID</th>
          <th>{{ $t("mobile.wms.name") }}</th>
          <th class="text-right">{{ $t("mobile.wms.priority") }}</th>
          <th>{{ $t("mobile.wms.status") }}</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="l in filtered" :key="l.id">
          <td>{{ l.id }}</td>
          <td class="m-wms-table__sku">{{ l.fullName }}</td>
          <td class="text-right">{{ l.priority }}</td>
          <td>
            <span :class="l.isActive ? 'm-wms-status--on' : 'm-wms-status--off'">
              {{ l.isActive ? $t("mobile.wms.active") : $t("mobile.wms.archived") }}
            </span>
          </td>
          <td class="text-right">
            <button v-if="canEdit" class="m-wms-rowbtn" @click="openEdit(l)"><i class="material-icons">edit</i></button>
            <button v-if="canArchive" class="m-wms-rowbtn" @click="toggleStatus(l)">
              <i class="material-icons">{{ l.isActive ? "archive" : "unarchive" }}</i>
            </button>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Bearbeiten / Neuanlage -->
    <m-sheet v-if="form" :title="form.id ? $t('mobile.wms.editLocation') : $t('mobile.wms.newLocation')" @close="form = null">
      <div class="m-wms-form" style="padding: 0">
        <div class="m-wms-form__field">
          <label>{{ $t("mobile.wms.warehouse") }} *</label>
          <select v-model="form.warehouseId" :disabled="!!form.id">
            <option v-for="w in warehouses" :key="w.id" :value="w.id">{{ w.name }}</option>
          </select>
        </div>
        <div class="m-wms-form__row">
          <div class="m-wms-form__field"><label>Ebene 1 *</label><input v-model="form.level1" maxlength="2" inputmode="numeric" /></div>
          <div class="m-wms-form__field"><label>Ebene 2 *</label><input v-model="form.level2" maxlength="2" inputmode="numeric" /></div>
          <div class="m-wms-form__field"><label>Ebene 3 *</label><input v-model="form.level3" maxlength="2" inputmode="numeric" /></div>
        </div>
        <div class="m-wms-form__field">
          <label>{{ $t("mobile.wms.priority") }} *</label>
          <input v-model.number="form.priority" type="number" inputmode="numeric" />
        </div>
        <div class="m-wms-actions">
          <button class="m-wms-actions__cancel" @click="form = null">{{ $t("mobile.wms.cancel") }}</button>
          <button class="m-wms-actions__save" :disabled="!formValid || saving" @click="save">{{ $t("mobile.wms.save") }}</button>
        </div>
      </div>
    </m-sheet>
  </div>
</template>

<script>
import WarehouseService from "@/services/warehouse.service";
import MSheet from "../components/MSheet.vue";
import { hasPermission } from "@/mixins/auth";

export default {
  name: "MLagerplaetze",
  components: { MSheet },
  data() {
    return {
      locations: [],
      warehouses: [],
      query: "",
      statusFilter: null,
      loading: false,
      form: null,
      saving: false,
    };
  },
  computed: {
    canCreate() {
      return hasPermission("Write", "StorageLocations", "Create");
    },
    canEdit() {
      return hasPermission("Write", "StorageLocations", "Edit");
    },
    canArchive() {
      return hasPermission("Write", "StorageLocations", "Archive");
    },
    filtered() {
      const q = this.query.trim().toUpperCase();
      return this.locations.filter((l) => this.statusFilter === null || l.isActive === this.statusFilter).filter((l) => !q || (l.fullName || "").toUpperCase().includes(q));
    },
    formValid() {
      const f = this.form;
      return !!(f && f.warehouseId && f.level1 && f.level2 && f.level3 && f.priority !== "" && f.priority !== null);
    },
  },
  created() {
    this.reload();
    WarehouseService.getWarehouses().then((w) => (this.warehouses = w || []));
  },
  methods: {
    async reload() {
      this.loading = true;
      try {
        // isPulldown false: auch archivierte Plaetze anzeigen (Statusfilter macht die Auswahl)
        this.locations = (await WarehouseService.getLocationsByWarehouse({ warehouseId: 0, location: "" })) || [];
      } finally {
        this.loading = false;
      }
    },
    openCreate() {
      this.form = { id: null, warehouseId: this.warehouses[0] ? this.warehouses[0].id : null, level1: "", level2: "", level3: "", priority: 100 };
    },
    openEdit(l) {
      this.form = { id: l.id, warehouseId: l.warehouseId, level1: l.level1, level2: l.level2, level3: l.level3, priority: l.priority };
    },
    async save() {
      if (!this.formValid || this.saving) return;
      this.saving = true;
      try {
        if (this.form.id) {
          await WarehouseService.updateStorageLocation({
            locationId: this.form.id,
            level1: this.form.level1,
            level2: this.form.level2,
            level3: this.form.level3,
            priority: this.form.priority,
            isActive: true,
          });
        } else {
          await WarehouseService.createStorageLocation({
            warehouseId: this.form.warehouseId,
            level1: this.form.level1,
            level2: this.form.level2,
            level3: this.form.level3,
            priority: this.form.priority,
            isActive: true,
          });
        }
        this.$notify({ timeout: 2500, message: this.$t("wms.messages.created"), horizontalAlign: "right", verticalAlign: "top", type: "success" });
        this.form = null;
        await this.reload();
      } catch (err) {
        this.$notify({ timeout: 3500, message: this.$t("mobile.wms.saveError"), horizontalAlign: "right", verticalAlign: "top", type: "danger" });
      } finally {
        this.saving = false;
      }
    },
    async toggleStatus(l) {
      try {
        await WarehouseService.changeStorageLocationStatus(l.id, l.isActive ? 0 : 1);
        await this.reload();
      } catch (err) {
        this.$notify({ timeout: 3500, message: this.$t("mobile.wms.saveError"), horizontalAlign: "right", verticalAlign: "top", type: "danger" });
      }
    },
  },
};
</script>

<style lang="scss" scoped src="./m-wms.scss"></style>

<style lang="scss" scoped>
.m-wms-fab {
  position: fixed;
  top: 64px;
  right: 14px;
  z-index: 20;
  width: 44px;
  height: 44px;
  border: none;
  border-radius: 50%;
  background: #7f915a;
  color: #fff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
}
.m-wms-rowbtn {
  border: none;
  background: transparent;
  padding: 2px;
  color: #607d8b;

  .material-icons {
    font-size: 18px;
  }
}
.m-wms-status--on {
  color: #7f915a;
  font-weight: 600;
}
.m-wms-status--off {
  color: #9e9e9e;
}
</style>
